import * as React from 'react';
import { Stack, Group, Text, Select, MultiSelect, Button, NumberInput, ActionIcon, Title, Divider, Popover, ColorPicker, ColorSwatch, Box, rem, useMantineColorScheme, ThemeIcon } from '@mantine/core';
import { Plus, Trash, Info, AlertCircle } from 'lucide-react';
import { ReportColumn, ColumnFormattingRule } from '@bi-studio/shared/lib';

interface ColumnFormattingProps {
    columns: ReportColumn[];
    onChange: (columns: ReportColumn[]) => void;
}

export const ColumnFormatting: React.FC<ColumnFormattingProps> = ({ columns, onChange }) => {
    const { colorScheme } = useMantineColorScheme();
    const numericColumns = columns.filter(c => c.dataType === 'numeric');

    if (numericColumns.length === 0) {
        return (
            <Box
                p={rem(40)}
                style={{
                    backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    borderRadius: 'var(--mantine-radius-md)',
                    border: '1px dashed var(--modal-border)',
                    textAlign: 'center'
                }}
            >
                <ThemeIcon size={40} radius="md" color="gray" variant="light" mb="md">
                    <AlertCircle size={24} />
                </ThemeIcon>
                <Title order={5} mb="xs">No Numeric Columns</Title>
                <Text size="sm" c="dimmed" maw={400} mx="auto">
                    Conditional formatting rules can only be applied to numeric columns. Ensure you have detected columns of type 'Numeric' in the Columns tab.
                </Text>
            </Box>
        );
    }

    const addRule = (colName: string) => {
        const newCols = columns.map(c => {
            if (c.name === colName) {
                const rules = [...(c.formattingConfig || [])];
                rules.push({
                    threshold: 0,
                    operator: '>',
                    backgroundColor: colorScheme === 'dark' ? 'rgba(46, 204, 113, 0.1)' : '#e6fffa',
                    textColor: colorScheme === 'dark' ? '#2ecc71' : '#2c7a7b',
                    targetColumns: [colName],
                    comparisonType: 'value'
                });
                return { ...c, formattingConfig: rules };
            }
            return c;
        });
        onChange(newCols);
    };

    const updateRule = (colName: string, ruleIdx: number, updatedRule: Partial<ColumnFormattingRule>) => {
        const newCols = columns.map(c => {
            if (c.name === colName) {
                const rules = [...(c.formattingConfig || [])];
                rules[ruleIdx] = { ...rules[ruleIdx], ...updatedRule };
                return { ...c, formattingConfig: rules };
            }
            return c;
        });
        onChange(newCols);
    };

    const removeRule = (colName: string, ruleIdx: number) => {
        const newCols = columns.map(c => {
            if (c.name === colName) {
                const rules = [...(c.formattingConfig || [])].filter((_, i) => i !== ruleIdx);
                return { ...c, formattingConfig: rules.length > 0 ? rules : undefined };
            }
            return c;
        });
        onChange(newCols);
    };

    return (
        <Stack gap="xl">
            <Box
                p="md"
                style={{
                    backgroundColor: colorScheme === 'dark' ? 'rgba(77, 171, 247, 0.1)' : 'rgba(231, 245, 255, 1)',
                    borderRadius: 'var(--mantine-radius-md)',
                    border: '1px solid var(--mantine-color-brand-light)'
                }}
            >
                <Group wrap="nowrap" align="flex-start" gap="sm">
                    <Info size={20} className="text-brand shrink-0" />
                    <Box>
                        <Text size="sm" fw={600} c="brand" mb={rem(4)}>Conditional Formatting</Text>
                        <Text size="sm">
                            Define rules to highlight cells based on their values. Rules are evaluated in order; the first matching rule will be applied.
                        </Text>
                    </Box>
                </Group>
            </Box>

            {numericColumns.map(col => (
                <Box
                    key={col.name}
                    p="md"
                    style={{
                        backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(255, 255, 255, 0.5)',
                        borderRadius: 'var(--mantine-radius-lg)',
                        border: '1px solid var(--modal-border)',
                        backdropFilter: 'blur(5px)',
                    }}
                >
                    <Stack gap="md">
                        <Group justify="space-between">
                            <Group gap="xs">
                                <Title order={5} size="sm">{col.displayName || col.name}</Title>
                                <Text size="xs" c="dimmed">({col.name})</Text>
                            </Group>
                            <Button
                                variant="light"
                                size="xs"
                                leftSection={<Plus size={14} />}
                                onClick={() => addRule(col.name)}
                                color="brand"
                            >
                                Add Rule
                            </Button>
                        </Group>

                        <Divider opacity={0.5} />

                        {(!col.formattingConfig || col.formattingConfig.length === 0) ? (
                            <Box py="sm" style={{ textAlign: 'center' }}>
                                <Text size="sm" c="dimmed" fs="italic">No formatting rules defined for this column.</Text>
                            </Box>
                        ) : (
                            <Stack gap="xs">
                                {/* Desktop labels - hidden on mobile */}
                                <Group gap="xs" px={4} wrap="nowrap" visibleFrom="sm">
                                    <Text size="xs" fw={600} w={80}>Operator</Text>
                                    <Text size="xs" fw={600} w={120}>Compare vs</Text>
                                    <Text size="xs" fw={600} w={100}>Value</Text>
                                    <Text size="xs" fw={600} w={200}>Target Columns</Text>
                                    <Text size="xs" fw={600} w={40} ta="center">Bg</Text>
                                    <Text size="xs" fw={600} w={40} ta="center">Text</Text>
                                    <Box w={30} />
                                </Group>
                                
                                {col.formattingConfig.map((rule, idx) => (
                                    <Box 
                                        key={idx} 
                                        p={{ base: 'xs', sm: 0 }}
                                        style={(theme) => ({
                                            border: '1px solid var(--modal-border)',
                                            borderRadius: 'var(--mantine-radius-md)',
                                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0,0,0,0.01)',
                                            [`@media (min-width: ${theme.breakpoints.sm})`]: {
                                                border: 'none',
                                                borderRadius: 0,
                                                backgroundColor: 'transparent'
                                            }
                                        })}
                                    >
                                        <Group align="center" gap="xs" wrap="wrap">
                                            <Stack gap={4} w={{ base: '100%', sm: 80 }}>
                                                <Text size="xs" fw={600} hiddenFrom="sm">Operator</Text>
                                                <Select
                                                    size="xs"
                                                    w="100%"
                                                    value={rule.operator}
                                                    onChange={(val) => updateRule(col.name, idx, { operator: val as any })}
                                                    data={[
                                                        { value: '>', label: '>' },
                                                        { value: '>=', label: '>=' },
                                                        { value: '<', label: '<' },
                                                        { value: '<=', label: '<=' },
                                                        { value: '==', label: '==' },
                                                        { value: '!=', label: '!=' },
                                                    ]}
                                                />
                                            </Stack>

                                            <Stack gap={4} w={{ base: '100%', sm: 120 }}>
                                                <Text size="xs" fw={600} hiddenFrom="sm">Compare vs</Text>
                                                <Select
                                                    size="xs"
                                                    w="100%"
                                                    value={rule.comparisonType || 'value'}
                                                    onChange={(val) => updateRule(col.name, idx, { comparisonType: val as any })}
                                                    data={[
                                                        { value: 'value', label: 'Fixed Value' },
                                                        { value: 'first_row', label: 'First Row' },
                                                        { value: 'last_row', label: 'Last Row' },
                                                    ]}
                                                />
                                            </Stack>

                                            <Stack gap={4} w={{ base: '100%', sm: 100 }}>
                                                <Text size="xs" fw={600} hiddenFrom="sm">Value</Text>
                                                {(rule.comparisonType === 'value' || !rule.comparisonType) ? (
                                                    <NumberInput
                                                        size="xs"
                                                        w="100%"
                                                        value={rule.threshold}
                                                        onChange={(val) => updateRule(col.name, idx, { threshold: typeof val === 'number' ? val : 0 })}
                                                    />
                                                ) : (
                                                    <Box
                                                        h={30}
                                                        w="100%"
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                                            borderRadius: 'var(--mantine-radius-sm)',
                                                            border: '1px solid var(--modal-border)'
                                                        }}
                                                    >
                                                        <Text size="xs" c="dimmed" fw={600}>{rule.comparisonType === 'first_row' ? 'First Row' : 'Last Row'}</Text>
                                                    </Box>
                                                )}
                                            </Stack>

                                            <Stack gap={4} w={{ base: '100%', sm: 200 }}>
                                                <Text size="xs" fw={600} hiddenFrom="sm">Target Columns</Text>
                                                <MultiSelect
                                                    size="xs"
                                                    w="100%"
                                                    value={rule.targetColumns || [col.name]}
                                                    onChange={(val) => updateRule(col.name, idx, { targetColumns: val })}
                                                    data={[
                                                        { value: col.name, label: 'This Column' },
                                                        { value: '__row__', label: 'Whole Row' },
                                                        ...columns
                                                            .filter(c => c.name !== col.name)
                                                            .map(c => ({ value: c.name, label: c.displayName || c.name }))
                                                    ]}
                                                    placeholder="Pick targets"
                                                    searchable
                                                    clearable
                                                />
                                            </Stack>

                                            <Group gap="md" w={{ base: '100%', sm: 'auto' }} justify="space-between">
                                                <Group gap="xs">
                                                    <Stack gap={4} align="center">
                                                        <Text size="xs" fw={600} hiddenFrom="sm">Bg</Text>
                                                        <Popover position="bottom" withArrow shadow="md" trapFocus>
                                                            <Popover.Target>
                                                                <ColorSwatch
                                                                    color={rule.backgroundColor || '#ffffff'}
                                                                    size={24}
                                                                    style={{ cursor: 'pointer' }}
                                                                    title="Select Background Color"
                                                                />
                                                            </Popover.Target>
                                                            <Popover.Dropdown p={8}>
                                                                <ColorPicker
                                                                    value={rule.backgroundColor}
                                                                    onChange={(val: string) => updateRule(col.name, idx, { backgroundColor: val })}
                                                                    format="hex"
                                                                />
                                                            </Popover.Dropdown>
                                                        </Popover>
                                                    </Stack>

                                                    <Stack gap={4} align="center">
                                                        <Text size="xs" fw={600} hiddenFrom="sm">Text</Text>
                                                        <Popover position="bottom" withArrow shadow="md" trapFocus>
                                                            <Popover.Target>
                                                                <ColorSwatch
                                                                    color={rule.textColor || '#000000'}
                                                                    size={24}
                                                                    style={{ cursor: 'pointer' }}
                                                                    title="Select Text Color"
                                                                />
                                                            </Popover.Target>
                                                            <Popover.Dropdown p={8}>
                                                                <ColorPicker
                                                                    value={rule.textColor}
                                                                    onChange={(val: string) => updateRule(col.name, idx, { textColor: val })}
                                                                    format="hex"
                                                                />
                                                            </Popover.Dropdown>
                                                        </Popover>
                                                    </Stack>
                                                </Group>

                                                <ActionIcon
                                                    color="red"
                                                    variant="subtle"
                                                    w={30}
                                                    onClick={() => removeRule(col.name, idx)}
                                                    size="sm"
                                                    mt={{ base: 20, sm: 0 }}
                                                >
                                                    <Trash size={16} />
                                                </ActionIcon>
                                            </Group>
                                        </Group>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </Box>
            ))}
        </Stack>
    );
};
